import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import Main from '../layouts/Main';
import { useApi } from '../contexts/Api';
import EditSiteContentDialog from '../components/EditSiteContentDialog';

const SiteContent = () => {
  const { siteContent, loadingSiteContent, fetchSiteContent } = useApi();
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  const handleEditClick = (content) => {
    setSelectedContent(content);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedContent(null);
  };

  const handleEditSave = async () => {
    setEditDialogOpen(false);
    setSelectedContent(null);
    await fetchSiteContent();
  };

  const filteredContent = siteContent?.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.content_key.toLowerCase().includes(searchLower) ||
      item.description.toLowerCase().includes(searchLower) ||
      item.value_en.toLowerCase().includes(searchLower) ||
      item.value_fin.toLowerCase().includes(searchLower) ||
      item.value_fr.toLowerCase().includes(searchLower) ||
      item.value_de.toLowerCase().includes(searchLower)
    );
  }) || [];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Main>
      <div className="page-header">
        <div>
          <h1 className="page-title">Site Content</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Site Content</li>
          </ol>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">Site Content Management</div>
            </div>
            <div className="card-body">
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {loadingSiteContent ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Content Key</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Values</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredContent.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {item.content_key}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              <Chip label="EN" size="small" color="primary" variant="outlined" />
                              <Chip label="FIN" size="small" color="primary" variant="outlined" />
                              <Chip label="FR" size="small" color="primary" variant="outlined" />
                              <Chip label="DE" size="small" color="primary" variant="outlined" />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDate(item.updated_at)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(item)}
                              title="Edit content"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {!loadingSiteContent && filteredContent.length === 0 && (
                <Box textAlign="center" py={5}>
                  <Typography variant="h6" color="text.secondary">
                    {searchTerm ? 'No content found matching your search.' : 'No site content available.'}
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      </div>

      <EditSiteContentDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        content={selectedContent}
        onSave={handleEditSave}
      />
    </Main>
  );
};

export default SiteContent;